import React, { useMemo, useState, useEffect } from 'react';
import styles from './WorkflowTimeline.module.css';
import { clsx } from 'clsx';
import { toast, Toaster } from 'react-hot-toast';

export interface WorkshopTransition {
  id_workshop: string;
  source_state: string;
  destination_state: string;
  timestamp: string; // ISO string
  id_user: string;
}

export interface LegacyStateMachineConfig {
  initial: string;
  states: { name: string;[key: string]: any }[];
}

export type StateMachineConfig = LegacyStateMachineConfig | Record<string, any>;

export interface WorkflowTimelineProps {
  steps?: string[]; // Ordered list of state names
  config?: StateMachineConfig;
  transitions: WorkshopTransition[];
  onStepClick?: (step: string, user: string | null) => void;
}

const formatDuration = (ms: number) => {
  if (ms < 1000) return "0s";
  const seconds = Math.floor((ms / 1000) % 60);
  const minutes = Math.floor((ms / (1000 * 60)) % 60);
  const hours = Math.floor((ms / (1000 * 60 * 60)) % 24);
  const days = Math.floor((ms / (1000 * 60 * 60 * 24)));

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m ${seconds}s`;
};

export const WorkflowTimeline: React.FC<WorkflowTimelineProps> = ({
  steps,
  config,
  transitions,
  onStepClick
}) => {
  const [now, setNow] = useState(Date.now());

  const workflowSteps = useMemo(() => {
    if (config) {
      if ('states' in config && Array.isArray((config as LegacyStateMachineConfig).states)) {
        return (config as LegacyStateMachineConfig).states.map(s => s.name);
      }
      return Object.keys(config);
    }
    return steps || [];
  }, [steps, config]);

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  const { times, lastUser, currentState, currentEntryTime } = useMemo(() => {
    const times: Record<string, number> = {};
    const lastUser: Record<string, string> = {};

    const sorted = [...transitions].sort((a, b) =>
      new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );

    let currentEntryTime: number | null = null;
    let currentState: string | null = null;

    sorted.forEach((t) => {
      const ts = new Date(t.timestamp).getTime();

      if (currentState && currentEntryTime !== null) {
        const duration = ts - currentEntryTime;
        times[currentState] = (times[currentState] || 0) + duration;
      }

      currentState = t.destination_state;
      currentEntryTime = ts;
      lastUser[t.destination_state] = t.id_user;
    });

    return { times, lastUser, currentState, currentEntryTime };
  }, [transitions]);

  const currentIndex = workflowSteps.indexOf(currentState || '');

  const handleStepClick = (step: string) => {
    const user = lastUser[step];
    if (onStepClick) {
      onStepClick(step, user || null);
    } else if (user) {
      toast.success(`Transitioned by User ID: ${user}`);
    } else {
      toast('No transition info for this step.');
    }
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'completed': return styles.stepCompleted;
      case 'in-progress': return styles.stepInProgress;
      case 'next': return styles.stepNext;
      case 'pending': return styles.stepPending;
      default: return styles.stepPending;
    }
  };

  return (
    <div className={styles.timelineContainer}>
      <Toaster position="bottom-center" />
      {workflowSteps.map((step, index) => {
        let status = 'pending';
        let isCompleted = false;

        if (currentIndex === -1) {
          status = 'pending';
        } else if (index < currentIndex) {
          status = 'completed';
          isCompleted = true;
        } else if (index === currentIndex) {
          status = 'in-progress';
        } else if (index === currentIndex + 1) {
          status = 'next';
        } else {
          status = 'pending';
        }

        let displayTime = '';
        if (status === 'completed') {
          const time = times[step];
          if (time !== undefined) {
            displayTime = formatDuration(time);
          }
        } else if (status === 'in-progress' && currentEntryTime !== null) {
          const time = now - currentEntryTime;
          const accumulated = times[step] || 0;
          displayTime = formatDuration(accumulated + time);
        }

        return (
          <div key={step} className={clsx(styles.stepWrapper, getStatusClass(status))}>
            <div className={clsx(styles.connector, { [styles.connectorCompleted]: isCompleted })}></div>
            <div
              className={styles.stepIndicator}
              onClick={() => handleStepClick(step)}
              title={`Step: ${step}`}
            >
              {index + 1}
            </div>
            <div className={styles.stepContent}>
              <div className={styles.stepTitle}>{step}</div>
              {displayTime && <div className={styles.stepTime}>{displayTime}</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default WorkflowTimeline;
